import { useState } from "react";
import { CheckCircle2, Loader2, PenLine } from "lucide-react";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StarPicker } from "@/components/site/StarPicker";

const blank = { name: "", rating: 0, text: "", vehicle: "", website: "" };
const REQUIRED = "This field is required";
const inputCls = "mt-1.5 h-12 w-full rounded-md border border-zinc-700 bg-[#121214] px-3 text-base text-[#FAF7F2] focus:border-[#FFD23F] focus:outline-none";
const Err = ({ msg, id }) => (msg ? <p className="mt-1.5 text-sm font-medium text-[#ff6b5e]" data-testid={id} role="alert">{msg}</p> : null);

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = REQUIRED;
  if (!f.rating) e.rating = REQUIRED;
  if (!f.text.trim()) e.text = REQUIRED;
  else if (f.text.trim().length < 10) e.text = "Please write at least 10 characters";
  return e;
};

export const ReviewForm = ({ onDone }) => {
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/reviews", f);
      onDone(data);
    } catch (err) {
      if (err.response?.status === 400) setErrors(fieldErrs(err));
      else setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="review-form">
      <input type="text" name="website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" data-testid="review-honeypot-input" />
      <div>
        <label htmlFor="review-name" className="text-sm font-medium text-zinc-200">Name</label>
        <input id="review-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="review-name-input" />
        <Err msg={errors.name} id="review-name-error" />
      </div>
      <div>
        <span className="text-sm font-medium text-zinc-200">Rating</span>
        <StarPicker value={f.rating} onChange={(rating) => setF({ ...f, rating })} prefix="review" />
        <Err msg={errors.rating} id="review-rating-error" />
      </div>
      <div>
        <label htmlFor="review-text" className="text-sm font-medium text-zinc-200">Your experience</label>
        <textarea id="review-text" rows={4} maxLength={1500} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={`${inputCls} h-auto py-3`} data-testid="review-text-input" />
        <Err msg={errors.text} id="review-text-error" />
      </div>
      <div>
        <label htmlFor="review-vehicle" className="text-sm font-medium text-zinc-200">Vehicle / job <span className="text-zinc-500">(optional)</span></label>
        <input id="review-vehicle" value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })} className={inputCls} data-testid="review-vehicle-input" />
      </div>
      {error && <p className="text-sm font-medium text-[#ff6b5e]" data-testid="review-submit-error" role="alert">{error}</p>}
      <button type="submit" disabled={busy} className="btn-gold h-14 w-full text-lg disabled:opacity-60" data-testid="review-submit-button">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />} Post review
      </button>
    </form>
  );
};

export const ReviewThanks = ({ message }) => (
  <p className="flex items-start gap-2 rounded-md border border-[#7ee08a]/40 bg-[#7ee08a]/10 p-4 text-base text-[#b9f5c0]" data-testid="review-thanks" role="status">
    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> {message}
  </p>
);

export const ReviewDialog = ({ onPublished }) => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const change = (v) => {
    setOpen(v);
    if (!v) setDone(null);
  };
  const finish = (data) => {
    setDone(data.message);
    if (data.status === "published") onPublished?.();
  };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogTrigger asChild>
        <button className="btn-gold h-14 text-lg" data-testid="leave-review-button"><PenLine className="h-5 w-5" /> Leave a Review</button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto rounded-lg border-zinc-700 bg-[#1A1A1E] text-[#FAF7F2]" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl">Leave a Review</DialogTitle>
          <DialogDescription className="text-zinc-400">Tell others how we did.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-5">
            <ReviewThanks message={done} />
            <button onClick={() => change(false)} className="btn-ghost h-12 w-full" data-testid="review-thanks-close-button">Close</button>
          </div>
        ) : (
          <ReviewForm onDone={finish} />
        )}
      </DialogContent>
    </Dialog>
  );
};
